import { P } from '@hrms/shared';
import { ComponentType, LazyExoticComponent, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RequireAuth, RequirePermission } from '@/auth/guards';
import { FullPageLoader, NotFoundState, PageSkeleton } from '@/components/states';
import { AppShell } from '@/layouts/AppShell';
import { pages } from './routes';

type Page = LazyExoticComponent<ComponentType>;

const standalone = (Page: Page) => (
  <Suspense fallback={<FullPageLoader />}>
    <Page />
  </Suspense>
);

const inShell = (Page: Page, anyOf?: string[]) => {
  const el = (
    <Suspense fallback={<PageSkeleton />}>
      <Page />
    </Suspense>
  );
  return anyOf ? <RequirePermission anyOf={anyOf}>{el}</RequirePermission> : el;
};

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/app/dashboard" replace /> },
  { path: '/login', element: standalone(pages.login) },
  { path: '/forgot-password', element: standalone(pages.forgot) },
  { path: '/reset-password', element: standalone(pages.reset) },
  { path: '/accept-invite', element: standalone(pages.accept) },
  { path: '/select-organization', element: standalone(pages.selectOrg) },
  { path: '/platform', element: standalone(pages.platform) },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { path: '/app', element: <Navigate to="/app/dashboard" replace /> },
      { path: '/app/dashboard', element: inShell(pages.dashboard) },
      { path: '/account/security', element: inShell(pages.security) },

      { path: '/app/organization', element: inShell(pages.company, [P.TENANT_READ]) },
      { path: '/app/organization/departments', element: inShell(pages.departments, [P.DEPARTMENT_READ]) },
      { path: '/app/organization/teams', element: inShell(pages.teams, [P.TEAM_READ]) },
      { path: '/app/organization/locations', element: inShell(pages.locations, [P.LOCATION_READ]) },
      { path: '/app/organization/calendars', element: inShell(pages.calendars, [P.CALENDAR_READ]) },
      { path: '/app/settings', element: inShell(pages.settings, [P.CONFIG_READ, P.CUSTOM_FIELD_MANAGE, P.DOCUMENT_CATEGORY_MANAGE]) },

      { path: '/app/users', element: inShell(pages.users, [P.USER_READ]) },
      { path: '/app/invitations', element: inShell(pages.invitations, [P.USER_INVITE]) },
      { path: '/app/roles', element: inShell(pages.roles, [P.ROLE_READ]) },

      { path: '/app/employees', element: inShell(pages.employees, [P.EMPLOYEE_READ]) },
      { path: '/app/employees/new', element: inShell(pages.employeeForm, [P.EMPLOYEE_CREATE]) },
      { path: '/app/employees/import', element: inShell(pages.employeeImport, [P.EMPLOYEE_IMPORT]) },
      { path: '/app/employees/change-requests', element: inShell(pages.changeRequests, [P.EMPLOYEE_CHANGE_REVIEW]) },
      { path: '/app/employees/:id', element: inShell(pages.employeeDetail, [P.EMPLOYEE_READ]) },
      { path: '/app/employees/:id/edit', element: inShell(pages.employeeForm, [P.EMPLOYEE_UPDATE]) },

      { path: '/app/onboarding', element: <Navigate to="/app/onboarding/cases" replace /> },
      { path: '/app/onboarding/cases', element: inShell(pages.onboardingCases, [P.ONBOARDING_CASE_READ]) },
      { path: '/app/onboarding/cases/:id', element: inShell(pages.onboardingCase) },
      { path: '/app/onboarding/templates', element: inShell(pages.onboardingTemplates, [P.ONBOARDING_TEMPLATE_READ]) },
      { path: '/app/onboarding/templates/:id', element: inShell(pages.onboardingTemplate, [P.ONBOARDING_TEMPLATE_READ]) },

      { path: '/app/leave', element: <Navigate to="/app/leave/requests" replace /> },
      { path: '/app/leave/requests', element: inShell(pages.leaveRequests, [P.LEAVE_REQUEST_READ, P.LEAVE_APPROVE]) },
      { path: '/app/leave/requests/:id', element: inShell(pages.leaveRequest) },
      { path: '/app/leave/calendar', element: inShell(pages.leaveCalendar, [P.LEAVE_REQUEST_READ]) },
      { path: '/app/leave/types', element: inShell(pages.leaveTypes, [P.LEAVE_TYPE_MANAGE, P.LEAVE_POLICY_READ]) },
      { path: '/app/leave/policies', element: inShell(pages.leavePolicies, [P.LEAVE_POLICY_MANAGE, P.LEAVE_POLICY_READ]) },
      { path: '/app/leave/policies/:id', element: inShell(pages.leavePolicy, [P.LEAVE_POLICY_MANAGE, P.LEAVE_POLICY_READ]) },
      { path: '/app/attendance', element: inShell(pages.teamAttendance, [P.ATTENDANCE_READ]) },
      { path: '/app/attendance/employees/:id', element: inShell(pages.employeeAttendance, [P.ATTENDANCE_READ]) },

      { path: '/app/assets', element: <Navigate to="/app/assets/inventory" replace /> },
      { path: '/app/assets/inventory', element: inShell(pages.assetInventory, [P.ASSET_READ]) },
      { path: '/app/assets/assignments', element: inShell(pages.assetAssignments, [P.ASSET_READ]) },
      { path: '/app/assets/tags', element: inShell(pages.assetConfig, [P.ASSET_CONFIG_MANAGE]) },
      { path: '/app/assets/audit', element: inShell(pages.assetAudit, [P.ASSET_AUDIT_VIEW]) },
      { path: '/app/assets/:id', element: inShell(pages.assetDetail, [P.ASSET_READ]) },

      { path: '/app/reports', element: inShell(pages.reports, [P.REPORT_VIEW]) },
      { path: '/app/audit', element: inShell(pages.audit, [P.AUDIT_VIEW]) },

      { path: '/self', element: inShell(pages.selfDashboard) },
      { path: '/self/profile', element: inShell(pages.selfProfile) },
      { path: '/self/onboarding', element: inShell(pages.selfOnboarding) },
      { path: '/self/leave', element: inShell(pages.selfLeave) },
      { path: '/self/attendance', element: inShell(pages.myAttendance) },
      { path: '/self/assets', element: inShell(pages.selfAssets) },
      { path: '/self/documents', element: inShell(pages.selfDocuments) },
      { path: '*', element: <NotFoundState /> },
    ],
  },
]);
